import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Truck,
    Plus,
    Settings,
    Trash2,
    Check,
    X,
    Star,
    Wifi,
    WifiOff,
    RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Valid Tunisian governorates for Intigo
const TUNISIAN_GOVERNORATES = [
    'Ariana', 'Béja', 'Ben Arous', 'Bizerte', 'Gabès', 'Gafsa', 'Jendouba',
    'Kairouan', 'Kasserine', 'Kebili', 'Kef', 'Mahdia', 'Manouba', 'Medenine',
    'Monastir', 'Nabeul', 'Sfax', 'Sidi Bouzid', 'Siliana', 'Sousse',
    'Tataouine', 'Tozeur', 'Tunis', 'Zaghouan'
];

export function DeliveryProvidersPage() {
    const { t } = useLanguage();
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null);
    const [testingId, setTestingId] = useState(null);
    const [testResults, setTestResults] = useState({});

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        type: 'intigo',
        isActive: true,
        apiKey: '',
        apiUrl: '',
        webhookSecret: '',
        pickupAddress: '',
        pickupCity: '',
        pickupSubDivision: '',
        uniqueIdentifier: ''
    });

    const fetchProviders = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/delivery-providers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProviders(data.data.providers);
            }
        } catch (error) {
            console.error('Failed to fetch providers:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProviders();
    }, [fetchProviders]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');

        const url = editingProvider
            ? `${API_URL}/delivery-providers/${editingProvider.id}`
            : `${API_URL}/delivery-providers`;

        try {
            // Pack provider-specific fields into config JSON
            const { uniqueIdentifier, ...restFormData } = formData;
            const submitData = {
                ...restFormData,
                config: {
                    ...(uniqueIdentifier && { uniqueIdentifier })
                }
            };

            const response = await fetch(url, {
                method: editingProvider ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submitData)
            });

            const data = await response.json();
            if (data.success) {
                setDialogOpen(false);
                fetchProviders();
                setEditingProvider(null);
                setFormData({
                    name: '',
                    type: 'intigo',
                    isActive: true,
                    apiKey: '',
                    apiUrl: '',
                    webhookSecret: '',
                    pickupAddress: '',
                    pickupCity: '',
                    pickupSubDivision: '',
                    uniqueIdentifier: ''
                });
            } else {
                toast.error(data.error || t('errorSaveProvider'));
            }
        } catch (error) {
            console.error('Error saving provider:', error);
            toast.error(t('errorSaveProvider'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirmDeleteProvider'))) return;

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_URL}/delivery-providers/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchProviders();
            }
        } catch (error) {
            console.error('Error deleting provider:', error);
        }
    };

    const handleSetDefault = async (id) => {
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/delivery-providers/${id}/default`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchProviders();
        } catch (error) {
            console.error('Error setting default provider:', error);
        }
    };

    const testConnection = async (id) => {
        setTestingId(id);
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_URL}/delivery-providers/${id}/test`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setTestResults(prev => ({
                ...prev,
                [id]: { success: data.success, message: data.message || (data.success ? t('successTest') : t('failedTest')) }
            }));
        } catch (error) {
            setTestResults(prev => ({
                ...prev,
                [id]: { success: false, message: t('failedTest') }
            }));
        } finally {
            setTestingId(null);
        }
    };

    const openEditDialog = (provider) => {
        setEditingProvider(provider);
        setFormData({
            name: provider.name,
            type: provider.type,
            isActive: provider.isActive,
            apiKey: provider.settings?.apiKey || '',
            apiUrl: provider.settings?.apiUrl || '',
            webhookSecret: provider.settings?.webhookSecret || '',
            pickupAddress: provider.settings?.pickupAddress || '',
            pickupCity: provider.settings?.pickupCity || '',
            pickupSubDivision: provider.settings?.pickupSubDivision || '',
            uniqueIdentifier: provider.settings?.uniqueIdentifier || provider.config?.uniqueIdentifier || ''
        });
        setDialogOpen(true);
    };

    const openCreateDialog = () => {
        setEditingProvider(null);
        setFormData({
            name: '',
            type: 'intigo',
            isActive: true,
            apiKey: '',
            apiUrl: '',
            webhookSecret: '',
            pickupAddress: '',
            pickupCity: '',
            pickupSubDivision: '',
            uniqueIdentifier: ''
        });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingProvider(null);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('deliveryProviders')}</h1>
                        <p className="text-muted-foreground">{t('deliveryProvidersSubtitle')}</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchProviders}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {t('refresh')}
                        </Button>
                        <Button onClick={openCreateDialog}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('addProvider')}
                        </Button>
                    </div>
                </div>

                {/* Providers List */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Truck className="h-5 w-5" />
                            {t('providersList')}
                        </CardTitle>
                        <CardDescription>{t('configuredProviders')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('name')}</TableHead>
                                    <TableHead>{t('type')}</TableHead>
                                    <TableHead>{t('status')}</TableHead>
                                    <TableHead>{t('default')}</TableHead>
                                    <TableHead>{t('test')}</TableHead>
                                    <TableHead className="text-right">{t('actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {providers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                                            {t('noProviders') || 'No providers found'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    providers.map((provider) => (
                                        <TableRow key={provider.id}>
                                            <TableCell className="font-medium">{provider.name}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{provider.type}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={provider.isActive ? "success" : "secondary"}>
                                                    {provider.isActive ? t('active') : t('inactive')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {provider.isDefault ? (
                                                    <span className="flex items-center text-green-600">
                                                        <Check className="h-4 w-4 mr-1" />
                                                        {t('yes')}
                                                    </span>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleSetDefault(provider.id)}
                                                        className="text-muted-foreground hover:text-primary"
                                                    >
                                                        {t('setDefault')}
                                                    </Button>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => testConnection(provider.id)}
                                                        disabled={testingId === provider.id}
                                                    >
                                                        {testingId === provider.id ? (
                                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            t('test')
                                                        )}
                                                    </Button>
                                                    {testResults[provider.id] && (
                                                        <span className={`text-xs ${testResults[provider.id].success ? 'text-green-600' : 'text-red-500'}`}>
                                                            {testResults[provider.id].success ? (
                                                                <Wifi className="h-3 w-3 inline mr-1" />
                                                            ) : (
                                                                <WifiOff className="h-3 w-3 inline mr-1" />
                                                            )}
                                                            {testResults[provider.id].success ? 'OK' : 'Fail'}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openEditDialog(provider)}
                                                    >
                                                        <Settings className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-red-600 border-red-600 hover:bg-red-50"
                                                        onClick={() => handleDelete(provider.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={closeDialog}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>{editingProvider ? t('editProvider') : t('newProvider')}</DialogTitle>
                            <DialogDescription>
                                {editingProvider ? t('configureProvider') : t('addProviderDesc')}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="name">{t('providerName')}</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="My Delivery Provider"
                                />
                            </div>

                            <div className="col-span-2 md:col-span-1 space-y-2">
                                <Label htmlFor="type">{t('providerType')}</Label>
                                <Select
                                    value={formData.type}
                                    onValueChange={(v) => setFormData({ ...formData, type: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="intigo">Intigo</SelectItem>
                                        <SelectItem value="x-delivery">X-Delivery</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="col-span-2 md:col-span-1 flex items-end pb-2">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        className="h-4 w-4 rounded border-gray-300"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                    <Label htmlFor="isActive">{t('active')}</Label>
                                </div>
                            </div>

                            <div className="col-span-2 border-t pt-4 mt-2">
                                <h3 className="font-semibold mb-2">{t('commonSettings')}</h3>
                            </div>

                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="apiKey">{t('apiKey')}</Label>
                                <Input
                                    id="apiKey"
                                    type="password"
                                    value={formData.apiKey}
                                    onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                                />
                            </div>

                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="apiUrl">{t('apiUrl')}</Label>
                                <Input
                                    id="apiUrl"
                                    value={formData.apiUrl}
                                    onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })}
                                    placeholder="https://api.provider.com/v1"
                                />
                            </div>

                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="webhookSecret">{t('webhookSecret')}</Label>
                                <Input
                                    id="webhookSecret"
                                    type="password"
                                    value={formData.webhookSecret}
                                    onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
                                />
                            </div>

                            {formData.type === 'x-delivery' && (
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="uniqueIdentifier">{t('uniqueIdentifier') || 'Unique Identifier (Store ID)'}</Label>
                                    <Input
                                        id="uniqueIdentifier"
                                        value={formData.uniqueIdentifier}
                                        onChange={(e) => setFormData({ ...formData, uniqueIdentifier: e.target.value })}
                                        placeholder="e.g. GRAN04652G2M627"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('uniqueIdentifierHelp') || 'Your store identifier provided by X-Delivery'}
                                    </p>
                                </div>
                            )}

                            <div className="col-span-2 border-t pt-4 mt-2">
                                <h3 className="font-semibold mb-2">{t('pickupInfo')}</h3>
                            </div>

                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="pickupAddress">{t('pickupAddress')}</Label>
                                <Input
                                    id="pickupAddress"
                                    value={formData.pickupAddress}
                                    onChange={(e) => setFormData({ ...formData, pickupAddress: e.target.value })}
                                />
                            </div>

                            <div className="col-span-1 space-y-2">
                                <Label htmlFor="pickupCity">{t('pickupGovernorate')}</Label>
                                <Select
                                    value={formData.pickupCity}
                                    onValueChange={(v) => setFormData({ ...formData, pickupCity: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TUNISIAN_GOVERNORATES.map(gov => (
                                            <SelectItem key={gov} value={gov}>{gov}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="col-span-1 space-y-2">
                                <Label htmlFor="pickupSubDivision">{t('pickupSubdivision')}</Label>
                                <Input
                                    id="pickupSubDivision"
                                    value={formData.pickupSubDivision}
                                    onChange={(e) => setFormData({ ...formData, pickupSubDivision: e.target.value })}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeDialog}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={handleSubmit}>
                                {editingProvider ? t('saveChanges') : t('createProvider')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
